import React, { useEffect, useState } from 'react';
import { Form, Input, Space, Popover, Button, FormInstance } from 'antd';
import InlineMarkdown from '../InlineMarkdown';
import { QuestionCircleOutlined } from '@ant-design/icons';
import useSearchGitRemoteRefQuery from '../../hooks/query/useSearchGitRemoteRefQuery';
import { Param } from '../../cliDataSchema';

interface RemoteRefInputProps {
  value: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  param: Param;
  form: FormInstance;
}

const RemoteRefInput: React.FC<RemoteRefInputProps> = ({
  value,
  handleChange,
  param,
  form,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validateStatus, setValidateStatus] = useState<
    'error' | 'warning' | undefined
  >();
  const [repoUrl, setRepoUrl] = useState(
    form.getFieldValue('external_project_url'),
  );

  const {
    data: gitRemoteRef,
    refetch,
    error,
  } = useSearchGitRemoteRefQuery({ repoUrl, internalRepo: false });

  useEffect(() => {
    if (!repoUrl) {
      setErrorMessage(null);
      setValidateStatus(undefined);
    } else if (error) {
      setErrorMessage(
        'Cannot access remote project. Unable to load remote ref.',
      );
      setValidateStatus('warning');
    } else {
      setErrorMessage(null);
      setValidateStatus(undefined);
    }
  }, [repoUrl, gitRemoteRef, error]);

  const handleBlur = () => {
    const newRepoUrl = form.getFieldValue('external_project_url');
    if (newRepoUrl !== repoUrl) {
      setRepoUrl(newRepoUrl);
      refetch();
    }
  };

  return (
    <Form.Item
      name={param.name}
      label={
        <Space size={0}>
          {param.osep_generator_ui_display_name}
          {param.help && (
            <Popover
              content={<InlineMarkdown>{param.help}</InlineMarkdown>}
              title={param.osep_generator_ui_display_name}
              trigger="click"
            >
              <Button
                size="middle"
                icon={<QuestionCircleOutlined />}
                type="text"
              />
            </Popover>
          )}
        </Space>
      }
      validateStatus={validateStatus}
      help={errorMessage}
      rules={
        param.required
          ? [
              {
                required: true,
                message: 'This field is required. ',
              },
            ]
          : undefined
      }
    >
      <Input value={value} onChange={handleChange} onBlur={handleBlur} />
    </Form.Item>
  );
};

export default RemoteRefInput;
